import type { ElevarProductProps } from "@hooks/useElevarEvents"
import type { GTMProductProps } from "@hooks/useEnhancedEcommerceEvents"

import { useMarketPricing } from "@hooks/useMarketPricing"
import { decodeShopifyId } from "@utils/decodeShopifyId"
import { formatContentfulVariant } from "@utils/index"
import { getMarketAmount } from "@utils/pricing"
import _ from "lodash-es"
import { useMemo } from "react"

type Args = {
  // Events and cart
  addToCart: (args: any) => void

  // Global data (for discount rule lookups)
  automaticDiscountData?: any[] // from globalData?.automaticDiscounts (optional)
  // Inputs for discounts
  automaticDiscounts: any[] // from Redux
  customerCountryCode?: string

  discountedProductsData: Record<string, any> // map of contentfulId -> product
  elevarOnAddToCart: (p: ElevarProductProps, list?: string) => void
  mode?: Mode

  multipleDevices?: string // precomputed string for PDP (optional for SOPDP)
  onAddProductToCart: (p: GTMProductProps) => void
  // Inputs for upsell
  productData: any // contentful product (with .upsell)

  shopifyProductId: string
}

type Mode = "PDP" | "SOPDP"

type Return = {
  allDiscountProducts: any[][]
  allDiscountProductsIndexIdMap: Record<number, string>
  automaticDiscountData: any[]
  discountProducts: any[][] // formatted/filtered products
  upsellProduct?: any // formatted upsell with onAddToCart on each variant
}

export function useDiscountAndUpsellProducts({
  addToCart,
  automaticDiscountData: automaticDiscountDataInput = [],
  automaticDiscounts,
  customerCountryCode = "US",
  discountedProductsData,
  elevarOnAddToCart,
  mode = "PDP",
  multipleDevices,
  onAddProductToCart,
  productData,
  shopifyProductId,
}: Args): Return {
  const decodedShopifyProductId = decodeShopifyId(shopifyProductId)

  const automaticDiscountData = automaticDiscountDataInput || []

  // Collect all variant SKUs for market pricing fetch
  const allVariantSkus = useMemo(() => {
    const skus: string[] = []
    // Collect SKUs from discounted products
    Object.values(discountedProductsData).forEach((product: any) => {
      product.variants?.forEach((variant) => {
        if (variant.sku) {
          skus.push(variant.sku)
        }
      })
    })
    // Collect SKUs from upsell product
    if (productData?.upsell?.variants) {
      productData.upsell.variants.forEach((variant) => {
        if (variant.sku) {
          skus.push(variant.sku)
        }
      })
    }
    return skus
  }, [discountedProductsData, productData])

  // Fetch market pricing data for all variants
  const { data: marketPricingData } = useMarketPricing(allVariantSkus)

  // Create pricing map for quick lookups
  const marketPricingMap = useMemo(() => {
    const map = new Map()
    marketPricingData.forEach((variant) => {
      map.set(variant.sku, variant.marketPrices)
    })
    return map
  }, [marketPricingData])

  // Merge market pricing into discounted products
  const discountedProductsWithPricing = useMemo(() => {
    const result: Record<string, any> = {}
    Object.entries(discountedProductsData).forEach(
      ([key, product]: [string, any]) => {
        result[key] = {
          ...product,
          variants: product.variants?.map((variant) => ({
            ...variant,
            marketPrices: marketPricingMap.get(variant.sku) || {},
          })),
        }
      }
    )
    return result
  }, [discountedProductsData, marketPricingMap])

  // Merge market pricing into upsell product
  const productDataWithPricing = useMemo(() => {
    if (!productData?.upsell) return productData
    return {
      ...productData,
      upsell: {
        ...productData.upsell,
        variants: productData.upsell.variants?.map((variant) => ({
          ...variant,
          marketPrices: marketPricingMap.get(variant.sku) || {},
        })),
      },
    }
  }, [productData, marketPricingMap])

  const { allDiscountProducts, allDiscountProductsIndexIdMap } = useMemo(() => {
    const result: any[] = []
    const indexMap: Record<number, string> = {}

    automaticDiscounts?.forEach((ad, i) => {
      const picked: any[] = []
      const isX = ad.customerBuysProducts?.includes(decodedShopifyProductId)
      const isY = ad.customerGetsProducts?.includes(decodedShopifyProductId)

      const ids = isX
        ? ad.customerGetsProducts
        : isY
          ? ad.customerBuysProducts
          : []

      ids?.forEach((id: string) => {
        const contentfulId = ad.idMap?.[id]
        if (contentfulId && discountedProductsWithPricing[contentfulId]) {
          picked.push(discountedProductsWithPricing[contentfulId])
        }
      })

      result.push(picked)
      indexMap[i] = ad.id
    })

    return {
      allDiscountProducts: result,
      allDiscountProductsIndexIdMap: indexMap,
    }
  }, [
    automaticDiscounts,
    discountedProductsWithPricing,
    decodedShopifyProductId,
  ])

  const resolveDeviceLabel = (variant?: any) => {
    if (mode === "SOPDP") {
      return (
        variant?.deviceName ||
        variant?.device?.name ||
        _.get(productData, "upsell.device.name") ||
        _.get(productData, "device.name")
      )
    }
    // PDP
    return multipleDevices
      ? multipleDevices.replace("#", ", ")
      : _.get(productData, "upsell.device.name") ||
          _.get(productData, "device.name")
  }

  const discountProducts = useMemo(() => {
    if (!allDiscountProducts?.length) return allDiscountProducts

    const allFiltered: any[] = []

    allDiscountProducts?.forEach((discountProductsAtIndex, i) => {
      let filtered = discountProductsAtIndex.filter(
        ({ customPrint, variants }) => {
          // SOPDP non-US: exclude Special Ops
          if (mode === "SOPDP" && customerCountryCode !== "US") {
            if (customPrint) return false
          }
          const hasImage = _.get(variants, "0.images.0.file.url")
          return variants && variants.length > 0 && !!hasImage
        }
      )

      // Apply discount rule’s matching logic
      const discountId = allDiscountProductsIndexIdMap[i]
      const parsedId = discountId?.replace?.(
        "gid://shopify/DiscountAutomaticNode/",
        ""
      )
      const discountConfig = automaticDiscountData?.find?.(
        (d: any) => d.discountId === parsedId
      )

      if (discountConfig) {
        if (productData?.device?.slug === "portable-charger") {
          filtered = filtered.filter((p: any) => !!p.builtInMagnet)
        } else if (discountConfig.matchDevice) {
          const hasMagnet = !!productData?.builtInMagnet
          filtered = filtered.filter((p: any) => {
            if (hasMagnet) {
              return (
                p?.device?.slug === productData?.device?.slug ||
                p?.device?.slug === "portable-charger"
              )
            }
            return p?.device?.slug === productData?.device?.slug
          })
        } else if (
          productData?.productSubType?.slug &&
          ["cases", "screen-protectors"].includes(
            productData?.productSubType?.slug
          )
        ) {
          filtered = filtered.filter((p: any) => {
            if (
              p?.productSubType &&
              ["cases", "screen-protectors"].includes(p.productSubType.slug)
            ) {
              return p?.device?.slug === productData?.device?.slug
            }
            return true
          })
        }
      }

      allFiltered.push(
        filtered.map((p: any) => {
          const imageSrc = _.get(p, "variants.0.images.0.file.url", "")
          const [cleanedSrc] = (imageSrc || "").split("?")
          const productImage = `https:${cleanedSrc}`
          const v0 = _.get(p, "variants.0", {})
          const productPrice = getMarketAmount(v0, customerCountryCode, "price")
          const productCompareAtPrice = getMarketAmount(
            v0,
            customerCountryCode,
            "compareAtPrice"
          )

          return {
            ...p,
            productCompareAtPrice,
            productImage,
            productPrice,
          }
        })
      )
    })

    return allFiltered
  }, [
    allDiscountProducts,
    allDiscountProductsIndexIdMap,
    automaticDiscountData,
    customerCountryCode,
    mode,
    productData?.device?.slug,
    productData?.builtInMagnet,
    productData?.productSubType?.slug,
  ])

  const upsellProduct = useMemo(() => {
    if (
      !productDataWithPricing?.upsell ||
      !productDataWithPricing?.upsellCallout
    )
      return undefined

    let productImage: string | undefined
    let productPrice: string | undefined
    let productCompareAtPrice: string | undefined

    const formattedVariants = productDataWithPricing.upsell.variants?.map(
      formatContentfulVariant
    )

    const variantsWithHandlers = formattedVariants?.map((v: any, i: number) => {
      if (i === 0) {
        const imageSrc = _.get(v, "images.0", "")
        const [cleanedSrc] = (imageSrc || "").split("?")
        productImage = `https:${cleanedSrc}`
        productPrice = getMarketAmount(v, customerCountryCode, "price")
        productCompareAtPrice = getMarketAmount(
          v,
          customerCountryCode,
          "compareAtPrice"
        )
      }

      const onAddToCart = (quantity: number) => {
        const deviceLabel = resolveDeviceLabel(v)

        const imageSrc = _.get(v, "images.0", "")
        const [cleanedSrc] = (imageSrc || "").split("?")

        addToCart({
          cartProduct: {
            brand: productDataWithPricing.upsell.brand?.name,
            builtInMagnet: !!productDataWithPricing?.builtInMagnet,
            bundleProduct: productDataWithPricing.upsell.bundleProduct,
            color: v.color,
            compareAtPrice: getMarketAmount(
              v,
              customerCountryCode,
              "compareAtPrice"
            ),
            contentfulProductId: productDataWithPricing.upsell.contentful_id,
            contentfulVariantId: v.contentfulVariantId,
            customPrint: productDataWithPricing.upsell.customPrint,
            device: deviceLabel,
            deviceType: productDataWithPricing.upsell.device?.type?.name,
            image: `https:${cleanedSrc}`,
            language: productDataWithPricing.upsell.language,
            marketPrices: v.marketPrices,
            price: getMarketAmount(v, customerCountryCode, "price"),
            productName: productDataWithPricing.upsell.productName,
            productSlug: productDataWithPricing.upsell.slug,
            productSubType: productDataWithPricing?.productSubType?.slug,
            quantity,
            shopifyProductId:
              productDataWithPricing.upsell.shopifyProduct?.shopifyProductId,
            shopifyVariantId: v.variantId,
            size: v.size,
            sku: v.sku,
            variantDevice: _.get(v, "device.name"),
            variantName: v.name,
          },
        })

        const gtmProduct: GTMProductProps = {
          brand: productDataWithPricing.upsell.brand?.name,
          device: deviceLabel,
          deviceType: productDataWithPricing.upsell.device?.type?.name,
          price: getMarketAmount(v, customerCountryCode, "price"),
          productId:
            productDataWithPricing.upsell.shopifyProduct?.shopifyProductId,
          productName: productDataWithPricing.upsell.productName,
          quantity,
          sku: v.sku,
          variantName: v.name,
        }
        onAddProductToCart(gtmProduct)

        const elevarProduct: ElevarProductProps = {
          brand: "Urban Armor Gear",
          device: deviceLabel,
          deviceType: productDataWithPricing.upsell.device?.type?.name,
          image: `https:${cleanedSrc}`,
          list: "upsell",
          price: getMarketAmount(v, customerCountryCode, "price"),
          productId:
            productDataWithPricing.upsell.shopifyProduct?.shopifyProductId,
          productName: productDataWithPricing.upsell.productName,
          quantity,
          sku: v.sku,
          variantId: v.variantId,
          variantName: v.name,
        }
        elevarOnAddToCart(elevarProduct, "upsell")
      }

      return { ...v, onAddToCart }
    })

    return {
      ...productDataWithPricing.upsell,
      productCompareAtPrice,
      productImage,
      productPrice,
      variants: variantsWithHandlers,
    }
  }, [
    productDataWithPricing?.upsell,
    productDataWithPricing?.builtInMagnet,
    productDataWithPricing?.productSubType?.slug,
    customerCountryCode,
    addToCart,
    onAddProductToCart,
    elevarOnAddToCart,
    mode,
    multipleDevices,
  ])

  return {
    allDiscountProducts,
    allDiscountProductsIndexIdMap,
    automaticDiscountData,
    discountProducts,
    upsellProduct,
  }
}
